import React, { useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Divider,
    Card,
    CardContent,
    Chip,
    useTheme,
    useMediaQuery,
    CssBaseline,
    ThemeProvider,
    alpha,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Grid,
    IconButton,
} from '@mui/material';
import {
    Security,
    PrivacyTip,
    DataUsage,
    Gavel,
    Update,
    CheckCircle,
    Info,
    Share,
    Cookie,
    ChildCare,
    Extension,
    Storage,
    People,
    ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Custom theme with JMK branding
// const theme = createTheme({
//     palette: {
//         primary: {
//             main: '#1a237e',
//             light: '#3949ab',
//             dark: '#0d1445',
//         },
//         secondary: {
//             main: '#ff6f00',
//             light: '#ffa040',
//             dark: '#c43e00',
//         },
//         background: {
//             default: '#f5f7fa',
//             paper: '#ffffff',
//         },
//         info: {
//             main: '#0288d1',
//             light: '#4fc3f7',
//         },
//         success: {
//             main: '#2e7d32',
//         },
//         warning: {
//             main: '#e65100',
//         },
//     },
//     typography: {
//         fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
//         h4: {
//             fontWeight: 600,
//             letterSpacing: '-0.01em',
//         },
//         h6: {
//             fontWeight: 500,
//         },
//     },
//     shape: {
//         borderRadius: 12,
//     },
//     components: {
//         MuiPaper: {
//             styleOverrides: {
//                 root: {
//                     backgroundImage: 'none',
//                 },
//             },
//         },
//     },
// });

// Section data
const sections = [
    {
        id: 1,
        title: 'Information We Collect',
        icon: <DataUsage />,
        color: '#1565c0',
        subsections: [
            { title: 'School Information', items: ['School name', 'Address', 'Contact numbers', 'Email addresses', 'Administrator details', 'Subscription and billing information'] },
            { title: 'Student Information', items: ['Name', 'Admission number', 'Date of birth', 'Class/section', 'Parent/guardian details', 'Attendance records', 'Examination results', 'Fee records', 'Certificates and documents uploaded by the school'] },
            { title: 'Staff Information', items: ['Name', 'Designation', 'Contact details', 'Attendance records', 'Payroll-related information'] },
            { title: 'Technical Information', items: ['IP address', 'Device information', 'Browser type', 'Operating system', 'Login activity', 'Usage logs'] },
        ],
    },
    {
        id: 2,
        title: 'How We Use Information',
        icon: <Info />,
        color: '#00838f',
        items: [
            'Provide and maintain the School Management System',
            'Create and manage user accounts',
            'Process attendance, examinations, and fee records',
            'Generate reports and certificates',
            'Send notifications via SMS, email, or app alerts',
            'Provide customer support',
            'Improve platform performance and security',
            'Comply with legal obligations',
        ],
    },
    {
        id: 3,
        title: 'Data Ownership',
        icon: <Storage />,
        color: '#2e7d32',
        highlight: true,
        content: 'All student, staff, academic, attendance, examination, fee, and institutional data entered into the platform remains the property of the respective school. JMKINFO does not claim ownership of school data.',
    },
    {
        id: 4,
        title: 'Sharing of Information',
        icon: <Share />,
        color: '#e65100',
        items: [
            'We do not sell personal information.',
            'Information may be shared only with:',
            '• The respective school and its authorized users',
            '• Third-party service providers (SMS, email, cloud hosting, payment gateways) required to operate the platform',
            '• Government authorities or law enforcement when legally required',
        ],
    },
    {
        id: 5,
        title: 'Data Security',
        icon: <Security />,
        color: '#00695c',
        items: [
            'Secure server infrastructure',
            'Encrypted data transmission (HTTPS/SSL)',
            'Role-based access control',
            'Password protection',
            'Regular security monitoring',
            'Backup procedures',
        ],
        note: 'However, no internet-based system can guarantee absolute security.',
    },
    {
        id: 6,
        title: 'Data Retention',
        icon: <Storage />,
        color: '#4a148c',
        items: [
            'For the duration of the school\'s active subscription',
            'As required by applicable laws',
            'For a limited backup and recovery period after termination',
            'After the retention period, data may be securely deleted.',
        ],
    },
    {
        id: 7,
        title: 'User Responsibilities',
        icon: <People />,
        color: '#bf360c',
        items: [
            'Maintaining confidential passwords',
            'Restricting access to authorized persons',
            'Keeping information accurate and updated',
            'Reporting unauthorized access immediately',
        ],
    },
    {
        id: 8,
        title: 'Cookies and Analytics',
        icon: <Cookie />,
        color: '#4e342e',
        items: [
            'Maintain user sessions',
            'Remember preferences',
            'Analyze platform usage',
            'Improve user experience',
        ],
    },
    {
        id: 9,
        title: "Children's Privacy",
        icon: <ChildCare />,
        color: '#4a148c',
        content: 'The platform is intended for use by educational institutions. Student information is collected and managed by schools. Schools are responsible for obtaining any parental or guardian consent required under applicable laws.',
    },
    {
        id: 10,
        title: 'Third-Party Services',
        icon: <Extension />,
        color: '#006064',
        items: [
            'SMS providers',
            'Email service providers',
            'Cloud hosting providers',
            'Payment gateways',
        ],
        note: 'These third parties may process limited information necessary to provide their services.',
    },
    {
        id: 11,
        title: 'Your Rights',
        icon: <Gavel />,
        color: '#1a237e',
        items: [
            'Access to their data',
            'Correction of inaccurate data',
            'Deletion of data (subject to legal obligations)',
            'Export of available records',
            'Restriction of certain processing activities',
        ],
    },
    {
        id: 12,
        title: 'Changes to This Policy',
        icon: <Update />,
        color: '#e65100',
        content: 'JMKINFO may update this Privacy Policy from time to time. Updated versions will be published on the platform with a revised "Last Updated" date.',
    },
    {
        id: 13,
        title: 'Governing Law',
        icon: <Gavel />,
        color: '#1a237e',
        content: 'This Privacy Policy shall be governed by the laws of India. Any disputes shall be subject to the jurisdiction of the courts in Nagpur, Maharashtra, India.',
    },
];

// Disable right-click globally
const disableRightClick = (e) => {
    e.preventDefault();
    return false;
};

// Disable keyboard shortcuts
const disableKeyboardShortcuts = (e) => {
    if (
        (e.ctrlKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key)) ||
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['i', 'I', 'j', 'J'].includes(e.key)) ||
        (e.metaKey && ['c', 'C', 'a', 'A', 'u', 'U', 's', 'S'].includes(e.key))
    ) {
        e.preventDefault();
        return false;
    }
};

// Disable text selection
const disableTextSelection = (e) => {
    const target = e.target;
    if (target && target.closest && target.closest('.no-select')) {
        e.preventDefault();
        return false;
    }
};

const PrivacyPolicy = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    useEffect(() => {
        document.addEventListener('contextmenu', disableRightClick);
        document.addEventListener('keydown', disableKeyboardShortcuts);
        document.addEventListener('selectstart', disableTextSelection);
        document.addEventListener('dragstart', disableTextSelection);

        const preventCopy = (e) => e.preventDefault();
        document.addEventListener('copy', preventCopy);
        document.addEventListener('cut', preventCopy);

        return () => {
            document.removeEventListener('contextmenu', disableRightClick);
            document.removeEventListener('keydown', disableKeyboardShortcuts);
            document.removeEventListener('selectstart', disableTextSelection);
            document.removeEventListener('dragstart', disableTextSelection);
            document.removeEventListener('copy', preventCopy);
            document.removeEventListener('cut', preventCopy);
        };
    }, []);

    const renderSectionContent = (section) => {
        if (section.subsections) {
            return (
                <Box sx={{ mt: 1 }}>
                    {section.subsections.map((sub, idx) => (
                        <Box key={idx} sx={{ mb: 2 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: section.color,
                                    mb: 0.5,
                                }}
                            >
                                {sub.title}
                            </Typography>
                            <List dense disablePadding>
                                {sub.items.map((item, i) => (
                                    <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                                •
                                            </Typography>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            primaryTypographyProps={{
                                                variant: 'body2',
                                                sx: { color: theme.palette.text.secondary },
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))}
                </Box>
            );
        }

        if (section.items) {
            return (
                <List dense disablePadding sx={{ mt: 1 }}>
                    {section.items.map((item, idx) => (
                        <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    {item.startsWith('•') ? '•' : '◆'}
                                </Typography>
                            </ListItemIcon>
                            <ListItemText
                                primary={item.replace('• ', '')}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    sx: {
                                        color: item.startsWith('•') ? theme.palette.text.primary : theme.palette.text.secondary,
                                        fontWeight: item.startsWith('•') ? 500 : 400,
                                    },
                                }}
                            />
                        </ListItem>
                    ))}
                </List>
            );
        }

        if (section.content) {
            return (
                <Typography
                    variant="body2"
                    sx={{
                        color: section.highlight ? theme.palette.primary.main : theme.palette.text.secondary,
                        fontWeight: section.highlight ? 500 : 400,
                        mt: 1,
                        lineHeight: 1.8,
                        ...(section.highlight && {
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }),
                    }}
                >
                    {section.content}
                </Typography>
            );
        }

        return null;
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box
                className="no-select"
                sx={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    minHeight: '100vh',
                    backgroundColor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Sticky Header */}
                {/* <AppBar
          position="sticky"
          elevation={0}
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
            borderBottom: `3px solid ${theme.palette.secondary.main}`,
            zIndex: 1100,
          }}
        >
          <Toolbar sx={{ py: isMobile ? 1 : 1.5, px: isMobile ? 2 : 4 }}> */}
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <PrivacyTip
                        sx={{
                            mr: 2,
                            fontSize: isMobile ? 28 : 36,
                            color: alpha('#fff', 0.9),
                        }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                        {/* <Typography
                  variant={isMobile ? 'subtitle1' : 'h5'}
                  sx={{
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}
                >
                  JMKINFO School Management System
                </Typography> */}
                        <IconButton onClick={() => navigate(-1)} color="primary">
                            <ArrowBack />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
                                Privacy Policy
                            </Typography>

                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            label="Effective: 2026"
                            size="small"
                            sx={{
                                backgroundColor: alpha(theme.palette.secondary.main, 0.25),
                                color: '#fff',
                                fontWeight: 500,
                                border: `1px solid ${alpha('#fff', 0.15)}`,
                                '& .MuiChip-label': { px: 1.5 },
                            }}
                        />
                        <Chip
                            label="Last Updated: 2026"
                            size="small"
                            sx={{
                                backgroundColor: alpha(theme.palette.info.main, 0.25),
                                color: '#fff',
                                fontWeight: 500,
                                border: `1px solid ${alpha('#fff', 0.15)}`,
                                '& .MuiChip-label': { px: 1.5 },
                                display: isMobile ? 'none' : 'flex',
                            }}
                        />
                    </Box>
                </Box>
                {/* </Toolbar>
        </AppBar> */}

                {/* Content */}
                <Container
                    maxWidth="lg"
                    sx={{
                        flex: 1,
                        py: { xs: 3, md: 5 },
                        px: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    {/* Intro Banner */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            mb: 4,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 32, mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
                                    JMKINFO Privacy Policy Overview
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                                    JMKINFO (“we,” “our,” “us,” or “Service Provider”) operates the JMKINFO School Management System,
                                    a cloud-based platform designed to help schools manage students, staff, attendance, examinations,
                                    fees, communication, payroll, certificates, reports, and related administrative activities.
                                </Typography>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8, mt: 1 }}>
                                    This Privacy Policy explains how we collect, use, store, protect, and disclose information when
                                    schools and authorized users use our platform.
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>

                    {/* Sections Grid */}
                    <Grid container spacing={3}>
                        {sections.map((section) => (
                            <Grid item xs={12} md={section.id === 1 ? 12 : 6} key={section.id}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(section.color, 0.12)}`,
                                        transition: 'all 0.2s ease-in-out',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                                            borderColor: alpha(section.color, 0.25),
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: 3, flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    backgroundColor: alpha(section.color, 0.1),
                                                    color: section.color,
                                                    mr: 1.5,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {section.icon}
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                                    fontWeight: 600,
                                                    color: section.color,
                                                }}
                                            >
                                                {section.id}. {section.title}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ mb: 1.5, borderColor: alpha(section.color, 0.08) }} />
                                        {renderSectionContent(section)}
                                        {section.note && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: 'block',
                                                    mt: 1.5,
                                                    color: theme.palette.warning.main,
                                                    fontStyle: 'italic',
                                                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                                                    p: 1,
                                                    borderRadius: 1,
                                                }}
                                            >
                                                ⚠ {section.note}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Footer */}
                    <Box
                        component="footer"
                        sx={{
                            mt: 5,
                            pt: 4,
                            pb: 2,
                            textAlign: 'center',
                            borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 400,
                                letterSpacing: '0.3px',
                            }}
                        >
                            © {new Date().getFullYear()} JMKINFO School Management System. All rights reserved.
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: alpha(theme.palette.text.secondary, 0.6),
                                display: 'block',
                                mt: 0.5,
                                fontStyle: 'italic',
                            }}
                        >
                            Developed with ❤️ in Nagpur, Maharashtra, India
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default PrivacyPolicy;